import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBusinessAuth } from '@/context/BusinessAuthContext';

const LOGO_WORDMARK = "/brand/disqover-wordmark-dark.png";

export default function PartnerVerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLink, user, refreshUser } = useBusinessAuth();
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');
  const [redirectTarget, setRedirectTarget] = useState('/partner');
  const verificationAttempted = useRef(false);

  useEffect(() => {
    // Prevent double verification attempts
    if (verificationAttempted.current) return;
    
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setError('No verification token found');
      return;
    }

    const verifyToken = async (tokenToVerify) => {
      verificationAttempted.current = true;
      
      try {
        const result = await verifyMagicLink(tokenToVerify);
        setStatus('success');
        
        // Refresh user data to get subscription status
        await refreshUser();
        
        // Determine redirect based on subscription status
        const userData = result.user;
        const hasSubscription = userData?.subscription || userData?.business_id;
        
        if (hasSubscription) {
          // User has subscription - go to portal dashboard
          setRedirectTarget('/portal');
          setTimeout(() => navigate('/portal'), 1500);
        } else {
          // New user - go to partner page to choose plan and checkout
          setRedirectTarget('/partner');
          setTimeout(() => navigate('/partner'), 1500);
        }
      } catch (err) {
        setStatus('error');
        setError(err.message || 'Verification failed. The link may have expired or already been used.');
      }
    };

    verifyToken(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Verify once on mount; ref guard prevents re-runs
  }, [searchParams]);

  // If already logged in and verification failed, offer to continue
  const handleContinue = () => {
    if (user) {
      navigate('/portal');
    } else {
      navigate('/partner/login');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="partner-verify-page">
      <div className="w-full max-w-md text-center">
        <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
          <img src={LOGO_WORDMARK} alt="Disqover" className="h-8 mx-auto mb-6" />
          
          {status === 'verifying' && (
            <>
              <Loader2 className="w-16 h-16 text-teal-600 animate-spin mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-slate-900 font-heading mb-2">
                Verifying your email...
              </h1>
              <p className="text-slate-600">Please wait while we sign you in</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-slate-900 font-heading mb-2">
                Email verified!
              </h1>
              <p className="text-slate-600">
                {redirectTarget === '/portal' 
                  ? 'Redirecting to your dashboard...' 
                  : 'Redirecting to choose a plan...'}
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-slate-900 font-heading mb-2">
                Verification failed
              </h1>
              <p className="text-slate-600 mb-6">{error}</p>
              <Button
                onClick={handleContinue}
                className="w-full h-12 rounded-full bg-teal-600 hover:bg-teal-700"
              >
                {user ? 'Continue to Portal' : 'Try again'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
